import {ref} from "vue";
import palettes from "@/core/services/palettes.generated";
import {customSwatch, customVariables, isHexColor} from "@/core/helpers/accentColor";

export type Theme = "light" | "dark";
// what the user chose: "auto" follows the system
export type ThemeMode = "auto" | Theme;

export const THEME_STORAGE_KEY = "theme";

// The theme lives in the attribute data-theme of the <html> element (public/index.html sets it before the
// app loads). The styles of a theme are written under [data-theme="dark"] / [data-theme="light"].
const readStored = (): Theme | null => {
    try {
        const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
        return stored === "light" || stored === "dark" ? stored : null;
    } catch (e) {
        return null;
    }
};

const systemTheme = (): Theme =>
    window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";

// The dark styles are one static file (public/css/theme.dark.css, built by "npm run theme:dark" from the sass of
// Metronic with the dark mode). It has the same selectors as the normal styles, so it is placed after them
// in <head>: where the two differ, the dark one wins. In the light theme the file is not loaded at all.
const DARK_LINK_ID = "theme-dark-css";

const applyDarkStyles = (on: boolean) => {
    const existing = document.getElementById(DARK_LINK_ID);

    if (!on) {
        if (existing) existing.remove();
        return;
    }

    const link = (existing as HTMLLinkElement) || document.createElement("link");
    link.id = DARK_LINK_ID;
    link.rel = "stylesheet";
    link.href = process.env.BASE_URL + "css/theme.dark.css?v=" + (process.env.VUE_APP_BUILD || "1");
    // appended (or moved) to the end of <head>, after the styles of the app
    document.head.appendChild(link);
};

// The band at the top of the page is a background picture of <body> (LayoutService); in the dark theme it is
// replaced by the dark drawing of the same band. The band ends in the colour of the page (#151521), and the body has
// the same colour below it, so there is no step where the picture stops.
const BAND_STYLE_ID = "theme-band-style";

const applyBand = () => {
    if (document.getElementById(BAND_STYLE_ID)) return;

    const style = document.createElement("style");
    style.id = BAND_STYLE_ID;
    style.textContent =
        'html[data-theme="dark"], html[data-theme="dark"] body, html[data-theme="dark"] #app, html[data-theme="dark"] .page, html[data-theme="dark"] .wrapper { background-color: #151521 !important; }' +
        'html[data-theme="dark"] body { background-image: url(' +
        process.env.BASE_URL +
        "media/patterns/tv-header-bg.dark.svg) !important; }";
    document.head.appendChild(style);
};

// ---- the palette: the colour of the accent (buttons, links, marks), apart from light / dark ----
// A palette is one static file (public/css/palette.<id>.css, built by "npm run theme:palettes") that is loaded after
// the styles of the theme. "default" is the blue of the site and has no file. "custom" is the colour that the user
// picked: its file has a CSS variable in every place of the accent, and the values of the variables are set on <html>
// (see core/helpers/accentColor.ts).
export const PALETTE_STORAGE_KEY = "palette";
export const CUSTOM_HEX_KEY = "palette-custom";
// the computed variables are kept too: public/index.html sets them before the app starts, without the maths
const CUSTOM_VARS_KEY = "palette-vars";
export const CUSTOM_DEFAULT = "#7c3aed";
export const PALETTES: {id: string; light: string; dark: string}[] = palettes;
const PALETTE_LINK_ID = "theme-palette-css";

const readCustomHex = (): string | null => {
    try {
        const hex = window.localStorage.getItem(CUSTOM_HEX_KEY);
        return isHexColor(hex) ? hex.toLowerCase() : null;
    } catch (e) {
        return null;
    }
};

const knownPalette = (id: string | null): string | null =>
    id === "custom" ? (readCustomHex() ? id : null) : id && id !== "default" && PALETTES.some((p) => p.id === id) ? id : null;

const readStoredPalette = (): string | null => {
    try {
        return knownPalette(window.localStorage.getItem(PALETTE_STORAGE_KEY));
    } catch (e) {
        return null;
    }
};

export const currentPalette = ref<string>(readStoredPalette() || "default");
// the colour that the user picked (also when another palette is in use now: the picker shows it)
export const customAccent = ref<string>(readCustomHex() || CUSTOM_DEFAULT);

// the variables of the custom colour are set on <html> (inline); they are removed when another palette is chosen
const setCustomVariables = (vars: Record<string, string> | null) => {
    const style = document.documentElement.style;
    for (let i = style.length - 1; i >= 0; i--) {
        const name = style[i];
        if (/^--pc[ld]-\d+(-rgb)?$/.test(name)) style.removeProperty(name);
    }
    if (vars) Object.keys(vars).forEach((name) => style.setProperty(name, vars[name]));
};

const applyPalette = (id: string) => {
    const existing = document.getElementById(PALETTE_LINK_ID) as HTMLLinkElement | null;

    if (id === "custom") setCustomVariables(customVariables(customAccent.value));
    else setCustomVariables(null);

    if (id === "default") {
        document.documentElement.removeAttribute("data-palette");
        if (existing) existing.remove();
        return;
    }

    document.documentElement.setAttribute("data-palette", id);
    const href = process.env.BASE_URL + "css/palette." + id + ".css?v=" + (process.env.VUE_APP_BUILD || "1");
    // the file that public/index.html has already asked for is kept (no second request)
    if (existing && existing.getAttribute("href") === href) return;

    const link = existing || document.createElement("link");
    link.id = PALETTE_LINK_ID;
    link.rel = "stylesheet";
    link.href = href;
    document.head.appendChild(link);
};

const remember = (id: string, vars: Record<string, string> | null) => {
    try {
        if (id === "default") window.localStorage.removeItem(PALETTE_STORAGE_KEY);
        else window.localStorage.setItem(PALETTE_STORAGE_KEY, id);

        if (vars) window.localStorage.setItem(CUSTOM_VARS_KEY, JSON.stringify(vars));
        else window.localStorage.removeItem(CUSTOM_VARS_KEY);
    } catch (e) {
        // storage blocked: the choice lives until the page is closed
    }
};

export const setPalette = (id: string) => {
    const next = knownPalette(id) || "default";
    currentPalette.value = next;
    applyPalette(next);
    remember(next, next === "custom" ? customVariables(customAccent.value) : null);
};

// the user picks any colour: the palette becomes "custom"
export const setCustomAccent = (hex: string) => {
    if (!isHexColor(hex)) return;

    customAccent.value = hex.toLowerCase();
    try {
        window.localStorage.setItem(CUSTOM_HEX_KEY, customAccent.value);
    } catch (e) {
        // storage blocked: the choice lives until the page is closed
    }
    currentPalette.value = "custom";
    applyPalette("custom");
    remember("custom", customVariables(customAccent.value));
};

// the accent as it is in the two themes: the dot of the picker and the charts
export const accentSwatch = (): {light: string; dark: string} => {
    if (currentPalette.value === "custom") return customSwatch(customAccent.value);
    return PALETTES.find((p) => p.id === currentPalette.value) || PALETTES[0];
};

const apply = (theme: Theme) => {
    applyBand();
    document.documentElement.setAttribute("data-theme", theme);
    document.documentElement.style.colorScheme = theme;
    applyDarkStyles(theme === "dark");
    // the palette must stay the last of the styles: where it is as strong as the dark styles, it has to win
    const paletteLink = document.getElementById(PALETTE_LINK_ID);
    if (paletteLink && paletteLink.nextElementSibling) document.head.appendChild(paletteLink);
};

// the current theme, for the switch and for the charts
export const currentTheme = ref<Theme>(readStored() || systemTheme());

// the choice of the user, for the picker in the settings
export const themeMode = ref<ThemeMode>(readStored() || "auto");

// the user chooses a theme: it is applied and remembered
export const setTheme = (theme: Theme) => {
    themeMode.value = theme;
    currentTheme.value = theme;
    apply(theme);
    try {
        window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch (e) {
        // storage blocked: the choice lives until the page is closed
    }
};

// "auto" is stored as no value at all (public/index.html reads the same key before the app starts)
export const setThemeMode = (mode: ThemeMode) => (mode === "auto" ? resetTheme() : setTheme(mode));

export const toggleTheme = () => setTheme(currentTheme.value === "dark" ? "light" : "dark");

// the user goes back to the setting of the system
export const resetTheme = () => {
    themeMode.value = "auto";
    try {
        window.localStorage.removeItem(THEME_STORAGE_KEY);
    } catch (e) {
        // nothing to remove
    }
    currentTheme.value = systemTheme();
    apply(currentTheme.value);
};

// Called once at the start of the app. While the user has not chosen, the theme follows the system.
export const initTheme = () => {
    currentPalette.value = readStoredPalette() || "default";
    applyPalette(currentPalette.value);
    themeMode.value = readStored() || "auto";
    currentTheme.value = readStored() || systemTheme();
    apply(currentTheme.value);

    if (window.matchMedia) {
        window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
            if (!readStored()) {
                currentTheme.value = systemTheme();
                apply(currentTheme.value);
            }
        });
    }
};
